import React, { useState, useCallback } from 'react';
import { makeStyles } from 'tss-react/mui';
import useDebouncedCallback from 'use-debounce/lib/useDebouncedCallback';
import { v4 as uuidv4 } from 'uuid';

import SearchIcon from '@mui/icons-material/Search';
import Autocomplete from '@mui/material/Autocomplete';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

// import { actionHelpers } from 'app-components/flux/core';
import { getLogger, tracker } from 'app-components/utils/core';

import billPresentmentApi from 'data/billPresentment/api';
import type { Biller, EnrollResponse } from 'data/billPresentment/types';
import { extractHostname } from 'data/billPresentment/utils';

import { StepEnum } from '../types';
import { PopularBillersGrid } from './PopularBillersGrid';

const log = getLogger('BillPresentment/SearchView');

const useStyles = makeStyles()((theme) => ({
  root: {
    width: '100%',
    height: 472,
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    marginBottom: 12,
  },
  centered: {
    marginTop: 16,
    textAlign: 'center',
    color: theme.palette.text.secondary,
  },
  grid: {
    position: 'absolute',
    width: '100%',
    top: 96,
    zIndex: 0,
    display: 'flex',
    flexDirection: 'column',
    alignContent: 'center',
  },

  selfCenter: {
    alignSelf: 'center',
  },
}));

interface SearchViewProps {
  setStep: (step: StepEnum) => void;
  setEnrollment: (data: EnrollResponse) => void;
  setBiller: (biller: Biller) => void;
  setGUID: (guid: string) => void;
}

const SearchView = ({ setEnrollment, setStep, setBiller, setGUID }: SearchViewProps) => {
  const { classes } = useStyles();
  const [hasSearched, setHasSearched] = useState(false);

  const [billers, setBillers] = useState<Biller[]>([]);

  const handleSelect = useCallback(
    async (biller: Biller) => {
      log.log('SELECTING: ', biller);
      if (biller?.id) {
        try {
          const response = await billPresentmentApi.getEnrollment(biller?.id);
          log.debug('GET enroll response', response);
          setBiller(biller);
          setEnrollment(response);
          setGUID(uuidv4().toUpperCase());
          setStep(StepEnum.LOGIN);
        } catch (e) {
          log.warn('issue getting enrollment parameters', e);
        }
      }
      tracker.track(tracker.events.addBillerSelect, { biller_id: biller?.id, biller_name: biller?.name });
    },
    [setBiller, setEnrollment, setGUID, setStep],
  );

  const [debouncedSearch] = useDebouncedCallback(
    async (searchVal: string) => {
      setHasSearched(true);
      try {
        const response = await billPresentmentApi.search(searchVal);
        // limit billers to fiServ (don't show BVA billers)
        const filteredBillers = response?.data?.filter((datum) => datum.mode === 'PARTNER') || [];
        setBillers(filteredBillers);
      } catch (e) {
        log.warn('error in search!', e);
        setBillers([]);
      }
    },
    300,
    { leading: true, trailing: true },
  );

  return (
    <div className={classes.root}>
      <Typography className={classes.centered} variant='titleMedium'>
        Search all Biller Institutions
      </Typography>
      <Autocomplete<Biller>
        onChange={(_e, value, _reason, _details) => {
          if (value) handleSelect(value);
        }}
        onInputChange={(_e, value, _details) => {
          if (value && value.length > 2) {
            debouncedSearch(value);
          } else {
            setBillers([]);
            setHasSearched(false);
          }
        }}
        options={billers}
        noOptionsText={hasSearched ? 'No billers found' : 'Start typing to search for billers'}
        getOptionLabel={(option) => option.name}
        filterSelectedOptions
        renderOption={(props, option, { selected }) => {
          const url = extractHostname(option);
          return (
            <MenuItem {...props} key={option.id} selected={selected} onClick={() => handleSelect(option)}>
              <ListItemText primary={option.name} secondary={url} />
            </MenuItem>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            autoComplete='one-time-code'
            slotProps={{
              input: {
                ...params.InputProps,
                endAdornment: (
                  <>
                    {params.InputProps.endAdornment}
                    <InputAdornment position='end' className={classes.selfCenter}>
                      <IconButton aria-label='Clear search' onClick={() => setBillers([])} size='large'>
                        <SearchIcon />
                      </IconButton>
                    </InputAdornment>
                  </>
                ),
                type: 'search',
              },
            }}
          />
        )}
        filterOptions={(x) => x} // need this to disable default filter and use QCS matching
      />

      {/* If we receive an endpoint for popular billers, they should come here */}
      <div className={classes.grid}>
        <Typography className={classes.centered} variant='titleMedium'>
          Or select from popular ones
        </Typography>
        <PopularBillersGrid handleSelect={handleSelect} />
      </div>
    </div>
  );
};

export default SearchView;
